import * as React from 'react';
import { Menu } from 'semantic-ui-react';
import { NombrePersonal as NombrePersonalDTO } from 'src/api/protocolo';
import { ItemProps } from 'src/componentes/CheckboxMenu';
import { objectFromEntries } from 'src/utilidades';
import { CursoDTO } from '../tipos';
import {
    DatosFiltro,
    datosFiltroInicial,
    Filtro,
    Filtros,
    selectorItemASelectorValue,
} from './Filtro';

const ASIGNATURAS = [
    { value: 'lenguaje', text: 'Lenguaje' },
    { value: 'matematica', text: 'Matemática' },
    { value: 'historia', text: 'Historia' },
    { value: 'ciencias', text: 'Ciencias' },
] as const;

const PLANES = [
    { value: 'anual', text: 'Anual' },
    { value: 'intensivo', text: 'Intensivo' },
    { value: 'intensivo-vespertino', text: 'Intensivo vesp.' },
    { value: 'electivo', text: 'Electivo' },
] as const;

type Asignatura = typeof ASIGNATURAS[number]['value'];

const primeraLetraAAsignatura: Record<string, Asignatura | undefined> = {
    M: 'matematica',
    L: 'lenguaje',
    H: 'historia',
    B: 'ciencias',
    Q: 'ciencias',
    F: 'ciencias',
};

function asignaturaDelBloque(bloque: string) {
    const asignatura = primeraLetraAAsignatura[bloque.charAt(0)];
    if (!asignatura) throw new Error(`El bloque ${bloque} no tiene asignatura conocida`);
    return asignatura;
}

type Props = Readonly<{
    cursos: readonly CursoDTO[];
    alFiltrarCursos: (cursos: CursoDTO[]) => void;
}>;

type State = Readonly<{
    planes: DatosFiltro;
    asignaturas: DatosFiltro;
    profesores: DatosFiltro;
    asistentes: DatosFiltro;
    salas: DatosFiltro;
}>;

// Estas funciones determinan como acumular los items de cada filtro en base a
// un curso. El resultado puede ser un arreglo en cuyo caso se añadirán todos.
// De todos modos, solo puede haber un item para cada value elegido de modo que
// un item posterior sobreescribirá a uno anterior con el mismo value.
const selectoresFiltros = {
    profesores: (curso: CursoDTO) =>
        curso.profesor
            ? {
                  value: curso.profesor.id,
                  text: nombrePersonalFormateado(curso.profesor.nombre),
              }
            : { value: '', text: 'Sin profesor asignado' },

    asistentes: (curso: CursoDTO) =>
        curso.asistentes.length > 0
            ? curso.asistentes.map(asistente => ({
                  value: asistente.id,
                  text: nombrePersonalFormateado(asistente.nombre),
              }))
            : { value: '', text: 'Sin asistente asignado' },

    salas: (curso: CursoDTO) => ({
        value: curso.sala,
        text: curso.sala,
    }),
};

class FiltrosParaCursos extends React.Component<Props, State> {
    public state: State = {
        planes: {
            items: PLANES,
            itemsVisibles: PLANES,
            values: objectFromEntries(PLANES.map(plan => [plan.value, true])),
        },
        asignaturas: {
            items: ASIGNATURAS,
            itemsVisibles: ASIGNATURAS,
            values: objectFromEntries(ASIGNATURAS.map(asig => [asig.value, true])),
        },
        profesores: datosFiltroInicial,
        asistentes: datosFiltroInicial,
        salas: datosFiltroInicial,
    };

    public componentDidMount() {
        this.inicializarFiltros();
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.props.cursos !== prevProps.cursos) {
            this.inicializarFiltros();
        }
    }

    public render() {
        return (
            <>
                <Menu.Item>
                    <Filtro
                        titulo={['Plan', 'planes']}
                        datos={this.state.planes}
                        cuandoDatosCambian={planes => this.filtrarCursos({ planes })}
                    />
                </Menu.Item>

                <Menu.Item>
                    <Filtro
                        titulo={['Asignatura', 'asignaturas']}
                        datos={this.state.asignaturas}
                        cuandoDatosCambian={asignaturas => this.filtrarCursos({ asignaturas })}
                    />
                </Menu.Item>

                <Menu.Item>
                    <Filtro
                        titulo={['Profesor', 'profesores']}
                        datos={this.state.profesores}
                        cuandoDatosCambian={profesores => this.filtrarCursos({ profesores })}
                    />
                </Menu.Item>

                <Menu.Item>
                    <Filtro
                        titulo={['Asistente', 'asistentes']}
                        datos={this.state.asistentes}
                        cuandoDatosCambian={asistentes => this.filtrarCursos({ asistentes })}
                    />
                </Menu.Item>

                <Menu.Item>
                    <Filtro
                        titulo={['Sala', 'salas']}
                        datos={this.state.salas}
                        cuandoDatosCambian={salas => this.filtrarCursos({ salas })}
                    />
                </Menu.Item>
            </>
        );
    }

    private inicializarFiltros() {
        // Los filtros se inicializan usando los selectores antes definidos y comparadores
        // para determinar el orden final para los items. La aserción está pues ItemProps
        // acepta que text sea cualquier ReactNode, aunque nosotros sabemos que siempre es
        // un string pues es lo que se hizo en los selectores
        const filtros = Filtros.inicializar(this.props.cursos, selectoresFiltros, {
            profesores: comparadorItem,
            asistentes: comparadorItem,
            salas: (a, b) => (a.text as string).localeCompare(b.text as string),
        });

        function comparadorItem(a: ItemProps, b: ItemProps) {
            if (a.value === '') return -1;
            if (b.value === '') return 1;
            return (a.text as string).localeCompare(b.text as string);
        }

        this.setState(filtros);
    }

    private filtrarCursos<K extends keyof State>(filtros: Pick<State, K>) {
        // Por cada nuevo filtro, actualizar este arreglo.
        const filtrosAAplicar = [
            this.filtrarPlanes,
            this.filtrarAsignaturas,
            this.crearFiltrador(
                'profesores',
                selectorItemASelectorValue(selectoresFiltros.profesores),
            ),
            this.crearFiltrador(
                'asistentes',
                selectorItemASelectorValue(selectoresFiltros.asistentes),
            ),
            this.crearFiltrador('salas', selectorItemASelectorValue(selectoresFiltros.salas)),
        ];

        this.setState(filtros, () => {
            const cursosFiltrados = filtrosAAplicar.reduce(
                (cursosFiltradosPrevios, funcion) => funcion(cursosFiltradosPrevios),
                this.props.cursos.slice(),
            );
            this.props.alFiltrarCursos(cursosFiltrados);
        });
    }

    private readonly filtrarPlanes = (cursos: readonly CursoDTO[]) => {
        return Filtros.filtrar(this.state.planes, cursos, curso => curso.plan);
    };

    private readonly filtrarAsignaturas = (cursos: readonly CursoDTO[]) => {
        return Filtros.filtrar(this.state.asignaturas, cursos, curso =>
            asignaturaDelBloque(curso.bloque),
        );
    };

    private crearFiltrador<K extends keyof State>(
        nombre: K,
        selector: (curso: CursoDTO) => string | readonly string[],
    ): (cursos: readonly CursoDTO[]) => CursoDTO[] {
        return cursos => {
            const datosFiltro = Filtros.recalcularItemsVisibles(
                this.state[nombre],
                cursos,
                selector,
            );

            this.setState(prev => ({
                ...prev,
                [nombre]: datosFiltro,
            }));

            return Filtros.filtrar(datosFiltro, cursos, selector);
        };
    }
}

export default FiltrosParaCursos;

function nombrePersonalFormateado(nombre: NombrePersonalDTO) {
    return [
        nombre.primerNombre,
        nombre.apellidoPaterno,
        `${nombre.apellidoMaterno.charAt(0)}.`,
    ].join(' ');
}



// WEBPACK FOOTER //
// ./src/modulos/Horarios/componentes/FiltrosParaCursos.tsx